// NewApplicantsBanner — "someone applied for a job, nobody has called them yet."
//
// OWNER, 2026-09-09: «ახალმა კანდიდატმა შეავსო ვაკანსიის ფორმა დაუკავშირდით რომ ჩანიშნოთ
// შეხვედრა» — shown on the Desk, to management only.
//
// A row leaves by itself because THE WORK HAPPENED — the candidate's status moved off 'new', an
// interview date was set, or (OWNER 2026-09-16) ANYTHING on the row was saved: `candidates.
// updated_at` is stamped by a DB trigger on every UPDATE (scheduler 0015), so a note, a phone
// fix, a district is a touch. Opening the card is not — only a save is.
//
// ✕ on EACH row (OWNER 2026-09-19: "can we add that x at the end to close manually" — then
// "instead we need each one removable separately"). This REVERSES the original no-dismiss rule
// (0427's), so it is deliberately narrow:
//   • it is per VIEWER and per BROWSER (localStorage) — it records nothing about the candidate,
//     so another manager still sees the applicant nobody called;
//   • it hides exactly that one applicant, by id — every other row, and every new arrival, stays.
// So the one failure this banner exists to stop (an applicant nobody called disappearing) can
// only happen for the person who pressed ✕, for the applicant they chose.
//
// SEEN (owner 2026-09-19: "make it also show if seen … different color on seen"): opening a
// candidate's card writes a `viewed` event to candidate_logs (CandidateModal). A row nobody has
// opened is drawn bright with a "new" dot; a seen row is muted and names who saw it and when.
// Seen is SHARED (any manager's view counts), unlike the ✕, which is per viewer.
//
// Each row names the manager it belongs to (OWNER 2026-09-19: "show which manager is assigned on
// the face"): the interviewer, else who the card was created for (a recruiter's ?mgr= link or a
// personal link stamps both), else "unassigned" — the pool.
//
// ⚠️ SELF-APPLIED ONLY. The filter is `apply_context is not null` — the column the public
// form writes (scheduler migration 0013) — NOT `source`, and not "status = new". A manager
// typing a candidate in by hand already knows about them; they do not need to be told. The
// distinction is only possible because the arrival evidence is stored as its own fact.
//
// Gated by the CALLER on `tab.candidates` (IntakeTab): hiring is management's, and an agent
// must not be handed a queue they cannot work.
import { useEffect, useState } from 'react'
import { Icon } from '../Icon.jsx'
import { t } from '../../lib/i18n.js'
import { getNewApplicants } from '../../db/index.js'
import { getCandidateViews } from '../../lib/agentLog.js'
import { fmtPhone } from '../../lib/phone.js'

const MAX_ROWS = 6
const DISMISS_KEY = 'sv-desk-applicants-dismissed'

function readDismissed() {
  try {
    const v = JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]')
    return new Set(Array.isArray(v) ? v : [])
  } catch { return new Set() }
}

export function NewApplicantsBanner({ enabled = false, rows: rowsProp, onOpenCandidate }) {
  // `rows` may be injected (the harness renders it outside the login gate; no fetch can
  // succeed there). Undefined = fetch our own.
  const [fetched, setFetched] = useState(null)
  const [dismissed, setDismissed] = useState(readDismissed)
  const [views, setViews] = useState({})
  const [retry, setRetry] = useState(0)
  const failed = rowsProp === undefined && !!fetched?.failed
  const all = rowsProp !== undefined ? rowsProp : (failed ? null : fetched)
  const idsKey = (all || []).map(c => c.id).join(',')

  // who has seen which applicant (the harness may inject `seen` on the rows instead)
  useEffect(() => {
    if (!enabled || !idsKey) return
    let alive = true
    getCandidateViews(idsKey.split(',')).then(v => { if (alive) setViews(v) }).catch(() => {})
    const onViewed = e => {
      const id = e?.detail?.id
      if (id) setViews(v => ({ ...v, [id]: { by: null, ts: new Date().toISOString() } }))
    }
    window.addEventListener('sentavus:candidate-viewed', onViewed)
    return () => { alive = false; window.removeEventListener('sentavus:candidate-viewed', onViewed) }
  }, [enabled, idsKey])

  useEffect(() => {
    if (rowsProp !== undefined || !enabled) return
    let alive = true
    // a failed read is NOT an empty queue (Desk home audit 2026-10-21) - it used to become [] and the banner vanished
    getNewApplicants().then(r => { if (alive) setFetched(r) })
      .catch(() => { if (alive) setFetched(f => (Array.isArray(f) && f.length) ? f : { failed: true }) })
    return () => { alive = false }
  }, [enabled, rowsProp !== undefined, retry])

  const rows = (all || []).filter(c => !dismissed.has(c.id))
  if (enabled && failed) return (
    <div className="mb-3 rounded border border-amber-500/40 bg-amber-500/5 px-3 py-1.5 text-xs text-amber-700 dark:text-amber-400" role="status">
      {t('desk_new_applicants_failed')}{' '}
      <button type="button" className="underline" onClick={() => { setFetched(null); setRetry(n => n + 1) }}>{t('desk_home_retry')}</button>
    </div>
  )
  if (!enabled || rows.length === 0) return null
  const n = rows.length

  const dismiss = (id) => {
    // keep only ids still waiting (plus this one), so the stored list cannot grow forever
    const waiting = new Set((all || []).map(c => c.id))
    const next = new Set([...dismissed].filter(x => waiting.has(x)))
    next.add(id)
    try { localStorage.setItem(DISMISS_KEY, JSON.stringify([...next])) } catch { /* private mode: dismiss for this view only */ }
    setDismissed(next)
  }
  const xCls = 'shrink-0 rounded px-1.5 py-1 text-xs leading-none text-emerald-700/60 hover:bg-emerald-500/15 hover:text-emerald-800 dark:text-emerald-400/60 dark:hover:text-emerald-300'

  return (
    <div className="mb-3 rounded border border-emerald-400/60 bg-emerald-50 px-3 py-2 dark:border-emerald-700/60 dark:bg-emerald-900/15">
      <div className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
        <Icon name="user-check" size={13} />{' '}
        {n === 1 ? t('desk_new_applicants_one') : t('desk_new_applicants_many').replace('{n}', n)}
      </div>
      <div className="mt-1.5 flex flex-col gap-0.5">
        {rows.slice(0, MAX_ROWS).map(c => {
          const manager = c.interviewer || c.created_by_name || null
          const seen = c.seen || views[c.id] || null
          const seenTitle = seen ? [t('desk_new_applicants_seen'), seen.by, seen.ts ? new Date(seen.ts).toLocaleString() : null].filter(Boolean).join(' · ') : t('desk_new_applicants_new')
          return (
            <div key={c.id} title={seenTitle}
              /* no opacity on seen rows (audit 2026-10-21: it pushed the applicant's text below readable contrast) -
                 the dot and the weight already say new vs seen */
              className={`flex items-center gap-1 rounded ${seen ? '' : 'bg-emerald-500/10'}`}>
            {/* the row and its ✕ are siblings: a button inside a button is invalid and swallows clicks */}
            <button type="button" onClick={() => onOpenCandidate?.(c.id)}
              className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5 rounded px-1 py-1 text-left text-xs hover:bg-emerald-500/10">
              {/* Line one never wraps internally: a long name truncates, the number stays beside it. */}
              <span className="flex min-w-0 flex-1 items-center gap-2">
              {/* the dot and the weight carry "new" vs "seen"; the colour is the second channel */}
              <span aria-hidden className={`size-2 shrink-0 rounded-full ${seen ? 'bg-muted-foreground/40' : 'bg-emerald-500'}`} />
              <span className={`min-w-0 truncate text-foreground ${seen ? '' : 'font-semibold'}`}>{c.name}</span>
              {/* The number is on the row because the ask is "call them" — reading it here is
                  often the whole job, and it saves opening the card to find out. */}
              {c.phone && <span className="shrink-0 tabular-nums text-muted-foreground">{fmtPhone(c.phone)}</span>}
              {c.position && <span className="hidden shrink-0 text-muted-foreground sm:inline">· {c.position}</span>}
              </span>
              {/* ⚠️ Own line on a phone (owner 2026-09-28): as one shrink-0 group beside the
                  number it ran past the banner's edge and under the ✕. basis-full breaks it onto
                  line two (indented past the dot); from sm it sits right-aligned on line one. */}
              <span className="flex min-w-0 basis-full flex-wrap items-center gap-x-2 pl-4 sm:ml-auto sm:basis-auto sm:pl-0">
                {/* who it belongs to: an unassigned applicant sits in the pool, and saying so is the
                    point — "nobody's" is the row most likely to be left uncalled */}
                <span className={`whitespace-nowrap text-[11px] ${manager ? 'text-foreground/70' : 'italic text-amber-700 dark:text-amber-400'}`}>
                  {manager ? <><Icon name="user" size={11} /> {manager}</> : t('desk_new_applicants_unassigned')}
                </span>
                {c.source && <span className="whitespace-nowrap text-[11px] text-muted-foreground/70">{c.source}</span>}
                <span className={`whitespace-nowrap text-[11px] ${seen ? 'text-muted-foreground' : 'font-medium text-emerald-700 dark:text-emerald-400'}`}>
                  {seen ? <>👁 {t('desk_new_applicants_seen')}{seen.by ? ` · ${seen.by}` : ''}</> : t('desk_new_applicants_new')}
                </span>
              </span>
            </button>
            <button type="button" onClick={() => dismiss(c.id)} title={t('desk_new_applicants_dismiss')}
              aria-label={`${t('desk_new_applicants_dismiss')}: ${c.name}`} className={xCls}>
              ✕
            </button>
            </div>
          )
        })}
        {n > MAX_ROWS && (
          <span className="px-1 pt-0.5 text-[11px] text-muted-foreground">
            {t('desk_new_applicants_more').replace('{n}', n - MAX_ROWS)}
          </span>
        )}
      </div>
    </div>
  )
}
